import { useCallback, useEffect, useState } from "react";
import { Box, Button, Container, Divider, Grid, Typography, useMediaQuery, useTheme } from "@mui/material";
import CustomRightImage from "@components/CustomRightImagev2";
import { InactivityHandler } from "@components/InactivityHandler";
import { RootState, useAppDispatch } from "@store/store";
import { useSelector } from "react-redux";
import { useLazyGetLoanApplicationByIdQuery, usePostValidateNextStageMutation } from "@api/applications";
import { useNavigate } from "react-router-dom";
import useStatusConfig from "@hooks/useStatusConfig";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { ApiError, DocumentResponse, PersonaStructure } from "@interfaces/response";
import * as Sentry from "@sentry/react";
import { useLazyGetMotorbikeCatalogByIdQuery } from "@api/catalogs";
import ButtonValidacionForm from "@components/ButtonValidacionForm";
import { useLazyGetPersonsQuery } from "@api/personasApi";
import CustomUploadFileV2 from "@components/CustomUploadFileV2";
import { UUID } from "node:crypto";
import { useLazyGetDocumentAppIdQuery } from "@api/documents";
import { DOCUMENTS_CATALOG } from "@helpers/constantes";
import { setParamsIdentityValidationForm } from "@store/slices/bofuSlice";

const SCREEN_STATUS = "CSAED";

const CredimotoExpediente = () => {
  // hooks
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { nextStatus } = useStatusConfig(SCREEN_STATUS);

  // redux hooks
  const application = useSelector((state: RootState) => state.register.application);
  const accountUser = useSelector((state: RootState) => state.register.account_user);
  const motorbikeInfo = useSelector((state: RootState) => state.register.motorbike);

  // api hooks
  const [getLoanApplicationById] = useLazyGetLoanApplicationByIdQuery();
  const [getMotorbikeCatalogById] = useLazyGetMotorbikeCatalogByIdQuery();
  const [triggerPostNextStage] = usePostValidateNextStageMutation();
  const [getPersonsByLoandId] = useLazyGetPersonsQuery();
  const [getDocumentsByAppId] = useLazyGetDocumentAppIdQuery();

  // state
  const [legalRepresentative, setLegalRepresentative] = useState<PersonaStructure | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDisabled, setIsDisabled] = useState(true);
  const [docComprobanteDomicilio, setDocComprobanteDomicilio] = useState<DocumentResponse | undefined>(undefined);
  const [docConstanciaSituacionFiscal, setDocConstanciaSituacionFiscal] = useState<DocumentResponse | undefined>(
    undefined,
  );
  const [docValidacionIdentidad, setDocValidacionIdentidad] = useState<DocumentResponse | undefined>(undefined);

  const handleContinue = async () => {
    try {
      if (!application.id || !motorbikeInfo.motorbikeCatalogId) {
        throw new Error("Application or motorbike catalog ID is required");
      }

      if (!nextStatus) {
        throw new Error("Next status is required");
      }

      setIsLoading(true);

      await triggerPostNextStage({
        applicationId: application.id,
        statusCode: nextStatus.code,
        username: `TOFU - ${accountUser.email}`,
      });

      // navigate(nextStatus.url);
    } catch (error) {
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
      Sentry.captureException(error, {
        extra: {
          component: "CredimotoAprobado-handleContinue",
        },
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClickIdentityValidation = () => {
    if (!legalRepresentative?.id) {
      return;
    }

    dispatch(
      setParamsIdentityValidationForm({
        person_id: legalRepresentative?.id,
        person_type: "REP_LEGAL",
      }),
    );
    setTimeout(() => {
      navigate("/producto/creditosimple/validacion_identidad_pf");
    }, 200);
  };

  const fetchInitialData = useCallback(async () => {
    try {
      if (!application.id) {
        throw new Error("Application not found");
      }

      if (!motorbikeInfo.motorbikeCatalogId) {
        throw new Error("Motorbike catalog ID is required");
      }

      setIsLoading(true);

      const [loanApplication, persons, documents, motorbikeCatalog] = await Promise.all([
        getLoanApplicationById(application.id).unwrap(),
        getPersonsByLoandId({ loanApplicationId: application.id }).unwrap(),
        getDocumentsByAppId({ loanApplicationId: application.id, status: true }).unwrap(),
        getMotorbikeCatalogById(motorbikeInfo.motorbikeCatalogId).unwrap(),
      ]);

      if (!loanApplication || !motorbikeCatalog) {
        throw new Error("Loan application not found");
      }

      if (persons.length === 0) {
        throw new Error("Persons not found");
      }

      // identify documents
      const docs_person = documents.filter((doc) => doc.person_id === persons[0].id);
      const doc_validacion_identidad = docs_person.find(
        (doc) => doc.file_code === DOCUMENTS_CATALOG.validacion_identidad.code,
      );
      const doc_comprobante_domicilio = docs_person.find(
        (doc) => doc.file_code === DOCUMENTS_CATALOG.comprobante_domicilio_personal.code,
      );
      const doc_constancia_situacion_fiscal = docs_person.find(
        (doc) => doc.file_code === DOCUMENTS_CATALOG.constancia_situacion_fiscal.code,
      );

      setDocComprobanteDomicilio(doc_comprobante_domicilio);
      setDocConstanciaSituacionFiscal(doc_constancia_situacion_fiscal);
      setDocValidacionIdentidad(doc_validacion_identidad);
      setLegalRepresentative(persons[0]);

      setIsDisabled(false);
    } catch (error) {
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
      Sentry.captureException(error, {
        extra: {
          component: "CredimotoAprobado-fetchInitialData",
        },
      });
    } finally {
      setIsLoading(false);
    }
  }, [
    application.id,
    dispatch,
    getDocumentsByAppId,
    getLoanApplicationById,
    getMotorbikeCatalogById,
    getPersonsByLoandId,
    motorbikeInfo.motorbikeCatalogId,
  ]);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  return (
    <Grid container height="100%">
      <Grid item xs={12} md={8} mt={2}>
        <InactivityHandler />
        <Box
          sx={{
            mt: 2,
            mx: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography variant={"tofu_title"} textAlign="center" color={"primary.dark"}>
            Expediente
          </Typography>

          <Container maxWidth="sm" sx={{ mt: isMobileOrTablet ? 3 : 4, textAlign: "center" }}>
            <Typography
              sx={{ color: "primary.dark", mb: 1 }}
              variant={isMobileOrTablet ? "body2" : "body1"}
              textAlign="center"
              fontWeight={500}
            >
              Estas a punto de finalizar, únicamente necesitamos tu validación de identidad y los siguientes documentos.
            </Typography>

            <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="body1" sx={{ flex: 1, textAlign: "left" }} color={theme?.palette?.primary?.dark}>
                Validación de identidad
              </Typography>

              <ButtonValidacionForm
                isCompleted={legalRepresentative?.identity_validation_completed}
                documentStatus={docValidacionIdentidad?.validated}
                onClick={() => handleClickIdentityValidation()}
              />
            </Box>

            <Divider sx={{ backgroundColor: "#A3D4E8", mt: 2 }} />

            <CustomUploadFileV2
              py={2}
              inputName={"comprobante_domicilio"}
              optionalName={"Comprobante de domicilio"}
              optionalDescription={"Debe corresponder a tu domicilio y haber sido emitido en los últimos 3 meses."}
              disabled={!legalRepresentative?.id}
              uploadedFile={docComprobanteDomicilio}
              setUploadedFile={(newValue) => {
                setDocComprobanteDomicilio(newValue);
              }}
              metadata={{
                fileCode: DOCUMENTS_CATALOG.comprobante_domicilio_personal.code,
                uploadType: "person",
                personId: (legalRepresentative?.id as UUID | undefined) ?? undefined,
                clientDetailsId: application?.client_details_id as UUID | undefined,
              }}
              required={true}
            />

            <CustomUploadFileV2
              py={2}
              inputName={"constanca_situacion_fiscal"}
              optionalName={"Constancia de situación fiscal"}
              optionalDescription={"Deberá haber sido emitida en los últimos 3 meses."}
              disabled={!legalRepresentative?.id}
              uploadedFile={docConstanciaSituacionFiscal}
              setUploadedFile={(newValue) => {
                setDocConstanciaSituacionFiscal(newValue);
              }}
              metadata={{
                fileCode: DOCUMENTS_CATALOG.constancia_situacion_fiscal.code,
                uploadType: "person",
                personId: (legalRepresentative?.id as UUID | undefined) ?? undefined,
                clientDetailsId: application?.client_details_id as UUID | undefined,
              }}
              required={true}
            />

            <Box display="flex" justifyContent="center" mt={isMobileOrTablet ? 2 : 3} mb={2}>
              <Button
                type="submit"
                variant="default"
                disabled={
                  isDisabled ||
                  isLoading ||
                  !docComprobanteDomicilio ||
                  !docConstanciaSituacionFiscal ||
                  !docValidacionIdentidad
                }
                onClick={handleContinue}
              >
                Continuar
              </Button>
            </Box>
          </Container>
        </Box>
      </Grid>

      {!isMobileOrTablet && (
        <Grid item xs={12} md={4}>
          <CustomRightImage />
        </Grid>
      )}
    </Grid>
  );
};

export default CredimotoExpediente;
